import type { FC, ReactNode } from 'react';
import { cn } from '../lib/utils';
import { AssistantMark } from './assistant-mark';
import { Message, MessageAvatar, MessageContent, MessageFooter } from './message';

interface ChatMessageProps {
  /** `user` renders a right-aligned bubble; `assistant` a marked answer column. */
  from: 'user' | 'assistant';
  children: ReactNode;
  /** Below the message: copy, retry, timestamps. */
  footer?: ReactNode;
  className?: string;
}

/**
 * One turn in a conversation. Content is free-form: pass plain text for the
 * user, and `Markdown`, `Trace` rows, tables or action buttons for the
 * Assistant. The column gaps give block children their rhythm.
 */
export const ChatMessage: FC<ChatMessageProps> = ({
  from,
  children,
  footer,
  className
}) => {
  if (from === 'user') {
    return (
      <Message align="end" className={cn('text-body-lg', className)}>
        <MessageContent className="items-end gap-1.5">
          <div className="max-w-[min(85%,36rem)] rounded-[16px_16px_4px_16px] border border-border bg-surface-hover px-4 py-3 leading-[1.4375rem] text-pretty whitespace-pre-wrap text-foreground">
            {children}
          </div>
          {footer ? <MessageFooter className="px-1">{footer}</MessageFooter> : null}
        </MessageContent>
      </Message>
    );
  }

  return (
    <Message align="start" className={cn('gap-4 max-sm:gap-3', className)}>
      <MessageAvatar className="mt-0.5 min-w-0 self-start overflow-visible bg-transparent group-has-data-[slot=message-footer]/message:translate-y-0">
        <AssistantMark size="sm" />
      </MessageAvatar>
      <MessageContent className="gap-4 text-body-lg leading-6 text-[var(--z-text-secondary)]">
        {children}
        {footer ? <MessageFooter className="-mt-1 px-0">{footer}</MessageFooter> : null}
      </MessageContent>
    </Message>
  );
};
